/**
 * useApplications Hook
 *
 * React Query hooks for fetching applications for a tenant.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { applicationService } from '../../../api/application.service';
import type {
  ApplicationSanitized,
  ApplicationListQuery,
} from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for fetching applications list
 */
export const useApplications = (
  tenantId: string,
  query?: ApplicationListQuery
): UseQueryResult<ApplicationSanitized[], Error> => {
  return useQuery<ApplicationSanitized[], Error>({
    queryKey: ['applications', tenantId, query],
    queryFn: async () => {
      console.log('[useApplications] Fetching applications for tenant:', tenantId);
      return await applicationService.listApplications(tenantId, query);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('[useApplications] Failed to fetch applications:', getErrorMessage(error));
    },
  });
};

/**
 * Hook for fetching a single application by ID
 */
export const useApplication = (
  tenantId: string,
  applicationId: string
): UseQueryResult<ApplicationSanitized, Error> => {
  return useQuery<ApplicationSanitized, Error>({
    queryKey: ['application', tenantId, applicationId],
    queryFn: async () => {
      console.log('[useApplication] Fetching application:', applicationId);
      return await applicationService.getApplication(tenantId, applicationId);
    },
    enabled: !!tenantId && !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      console.error('[useApplication] Failed to fetch application:', getErrorMessage(error));
    },
  });
};
